/**
 * PAS region vision via AppFunctor → pas-math-pkp /region-vision.
 * AppFunctor crops OSS; FC runs qwen3.7-plus.
 */

import { apiClient } from './apiClient';

export type PasCropRectNormalized = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type PasRegionVisionRequest = {
  assetId: string;
  regionId: string;
  hatchPrompt?: string;
  sourceKpId?: string | null;
  /** Route AppFunctor → pas-{subject}-pkp FC. */
  subject?: string | null;
  cropRectNormalized: PasCropRectNormalized;
};

export type PasRegionVisionResult = {
  success: boolean;
  regionContent: string;
  regionFeedback: string;
  regionIncomplete?: boolean;
  regionMissingHints?: string[];
  cropImageUrl?: string | null;
  model?: string | null;
  error?: string;
  message?: string;
};

export type PasCropPreviewResult = {
  success: boolean;
  cropImageUrl: string | null;
  error?: string;
  message?: string;
};

/** Fresh OSS crop signed URL for thumbnail / lightbox (no LLM). */
export async function requestPasCropPreview(input: {
  assetId: string;
  regionId?: string;
  cropRectNormalized: PasCropRectNormalized;
}): Promise<PasCropPreviewResult> {
  try {
    const payload = await apiClient.requestJson<{
      success?: boolean;
      data?: { cropImageUrl?: string | null };
      error?: string;
      message?: string;
    }>(
      '/api/pas/math-pkp/crop-preview',
      {
        method: 'POST',
        auth: true,
        body: {
          assetId: input.assetId,
          regionId: input.regionId,
          cropRectNormalized: input.cropRectNormalized,
        },
        timeoutMs: 30_000,
      },
    );
    const url = String(payload.data?.cropImageUrl || '').trim();
    return {
      success: Boolean(payload.success && url),
      cropImageUrl: url || null,
      error: payload.success ? undefined : (payload.error || 'crop_preview_failed'),
      message: payload.message,
    };
  } catch (error) {
    const err = error as Error & { payload?: unknown };
    const payload = (err.payload && typeof err.payload === 'object')
      ? err.payload as { message?: string; error?: string; data?: { cropImageUrl?: string } }
      : null;
    return {
      success: false,
      cropImageUrl: payload?.data?.cropImageUrl || null,
      error: payload?.error || 'network_error',
      message: payload?.message || err.message || '裁切预览失败',
    };
  }
}

export async function requestPasRegionVision(
  input: PasRegionVisionRequest,
): Promise<PasRegionVisionResult> {
  try {
    const payload = await apiClient.requestJson<{
      success?: boolean;
      data?: {
        regionContent?: string;
        regionFeedback?: string;
        regionIncomplete?: boolean;
        regionMissingHints?: string[];
        cropImageUrl?: string | null;
        model?: string | null;
      };
      error?: string;
      message?: string;
    }>(
      '/api/pas/math-pkp/region-vision',
      {
        method: 'POST',
        auth: true,
        body: {
          assetId: input.assetId,
          regionId: input.regionId,
          sourceKpId: input.sourceKpId ?? null,
          hatchPrompt: input.hatchPrompt || undefined,
          theme: input.hatchPrompt || undefined,
          subject: input.subject || undefined,
          cropRectNormalized: input.cropRectNormalized,
        },
        timeoutMs: 150_000,
      },
    );

    return {
      success: Boolean(payload.success && payload.data?.regionContent),
      regionContent: payload.data?.regionContent || '',
      regionFeedback: payload.data?.regionFeedback || payload.message || '',
      regionIncomplete: Boolean(payload.data?.regionIncomplete),
      regionMissingHints: Array.isArray(payload.data?.regionMissingHints)
        ? payload.data!.regionMissingHints!
        : [],
      cropImageUrl: payload.data?.cropImageUrl || null,
      model: payload.data?.model ?? null,
      error: payload.success ? undefined : (payload.error || 'region_vision_failed'),
      message: payload.message,
    };
  } catch (error) {
    const err = error as Error & { payload?: unknown };
    const payload = (err.payload && typeof err.payload === 'object')
      ? err.payload as {
        message?: string;
        error?: string;
        data?: { regionContent?: string; regionFeedback?: string; cropImageUrl?: string };
      }
      : null;
    return {
      success: false,
      regionContent: payload?.data?.regionContent || '',
      regionFeedback: payload?.data?.regionFeedback || payload?.message || err.message || '识图失败',
      cropImageUrl: payload?.data?.cropImageUrl || null,
      regionIncomplete: true,
      error: payload?.error || 'network_error',
      message: payload?.message || err.message || '识图失败',
    };
  }
}
